// src/Shop/CheckoutPage.js

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { CartContext } from "../context/CartContext";
import { getProductById } from "../Services/productService";
import { createOrder } from "../Services/orderService";
import "./CheckoutPage.css";

const POST_SHIPPING_COST = 100000;

const PAYMENT_INFO = {
  cardNumber: "6037-7011-1523-7676",
  cardOwner: "پریسا عبداله پور",
  receiptPhone: "09129518635",
  receiptChannels: "بله، روبیکا یا تلگرام",
};

const initialAddressForm = {
  receiverName: "",
  mobile: "",
  province: "",
  city: "",
  fullAddress: "",
  plate: "",
  unit: "",
  postalCode: "",
};

export default function CheckoutPage({ navigateTo }) {
  const { t, i18n } = useTranslation();

  const {
    user,
    isAuthenticated,
    authLoading,
    updateCurrentUser,
    refreshCurrentUser,
  } = useAuth();

  const { shoppingCart, emptyShoppingCart, replaceShoppingCart } =
    useContext(CartContext);

  const isEnglish = i18n.language?.startsWith("en");
  const pageDir = isEnglish ? "ltr" : "rtl";
  const locale = isEnglish ? "en-US" : "fa-IR";

  const tr = (key, defaultValue) => {
    return t(`checkout.${key}`, { defaultValue });
  };

  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [shippingMethod, setShippingMethod] = useState("");
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressMode, setAddressMode] = useState("list");
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressForm, setAddressForm] = useState(initialAddressForm);
  const [addressErrors, setAddressErrors] = useState({});
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutSuccess, setCheckoutSuccess] = useState("");
  const [paymentGuideVisible, setPaymentGuideVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stockModal, setStockModal] = useState(null);

  useEffect(() => {
    if (!user) return;

    setProfileForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      phone: user.phone || "",
    });

    const userAddresses = Array.isArray(user.addresses) ? user.addresses : [];
    setAddresses(userAddresses);

    const defaultAddress =
      userAddresses.find((address) => address.isDefault) || userAddresses[0];

    if (defaultAddress) {
      setSelectedAddressId(defaultAddress.id);
    }
  }, [user]);

  const selectedAddress = useMemo(() => {
    return addresses.find((address) => address.id === selectedAddressId) || null;
  }, [addresses, selectedAddressId]);

  const itemsTotal = useMemo(() => {
    return shoppingCart.reduce((total, product) => {
      return total + Number(product.price || 0) * Number(product.quantity || 0);
    }, 0);
  }, [shoppingCart]);

  const discount = 0;

  const shippingCost = shippingMethod === "post" ? POST_SHIPPING_COST : 0;
  const finalTotal = itemsTotal + shippingCost - discount;

  const deliveryTime =
    shippingMethod === "post"
      ? tr("threeBusinessDays", "حدود ۳ روز کاری")
      : shippingMethod === "courier"
      ? tr("today", "امروز")
      : "-";

  const shippingTitle =
    shippingMethod === "post"
      ? tr("expressPost", "پست پیشتاز")
      : shippingMethod === "courier"
      ? tr("sameDayCourier", "پیک همان روز")
      : "";

  const formatPrice = (value) => {
    return Number(value || 0).toLocaleString(locale);
  };

  const normalizeDigits = (value) => {
    return String(value || "").replace(/[^\d]/g, "");
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      setProfileForm((prev) => ({
        ...prev,
        [name]: normalizeDigits(value).slice(0, 11),
      }));
      return;
    }

    setProfileForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateProfile = () => {
    const firstName = profileForm.firstName.trim();
    const lastName = profileForm.lastName.trim();
    const phone = normalizeDigits(profileForm.phone);

    if (!firstName || !lastName || !phone) {
      setCheckoutError(
        tr("completeProfileError", "لطفاً اطلاعات خریدار را کامل کنید")
      );
      return false;
    }

    if (!/^[0-9]{11}$/.test(phone)) {
      setCheckoutError(
        tr("invalidMobileError", "شماره موبایل باید ۱۱ رقم باشد")
      );
      return false;
    }

    return true;
  };

  const openAddressList = () => {
    setAddressModalOpen(true);
    setAddressMode("list");
    setAddressErrors({});
    setEditingAddressId(null);
    setAddressForm(initialAddressForm);
  };

  const openNewAddressForm = () => {
    setAddressMode("form");
    setEditingAddressId(null);
    setAddressForm({
      ...initialAddressForm,
      receiverName:
        `${profileForm.firstName || ""} ${profileForm.lastName || ""}`.trim(),
      mobile: profileForm.phone || "",
    });
    setAddressErrors({});
  };

  const openEditAddressForm = (address) => {
    setAddressMode("form");
    setEditingAddressId(address.id);
    setAddressForm({
      receiverName: address.receiverName || "",
      mobile: address.mobile || "",
      province: address.province || "",
      city: address.city || "",
      fullAddress: address.fullAddress || "",
      plate: address.plate || "",
      unit: address.unit || "",
      postalCode: address.postalCode || "",
    });
    setAddressErrors({});
  };

  const handleAddressFormChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      setAddressForm((prev) => ({
        ...prev,
        [name]: normalizeDigits(value).slice(0, 11),
      }));
    } else if (name === "postalCode") {
      setAddressForm((prev) => ({
        ...prev,
        [name]: normalizeDigits(value).slice(0, 10),
      }));
    } else {
      setAddressForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (addressErrors[name]) {
      setAddressErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateAddressForm = () => {
    const errors = {};

    if (!addressForm.receiverName.trim()) {
      errors.receiverName = tr("requiredField", "این فیلد الزامی است");
    }

    if (!addressForm.mobile.trim()) {
      errors.mobile = tr("requiredField", "این فیلد الزامی است");
    }

    if (!addressForm.province.trim()) {
      errors.province = tr("requiredField", "این فیلد الزامی است");
    }

    if (!addressForm.city.trim()) {
      errors.city = tr("requiredField", "این فیلد الزامی است");
    }

    if (!addressForm.fullAddress.trim()) {
      errors.fullAddress = tr("requiredField", "این فیلد الزامی است");
    }

    if (!addressForm.plate.trim()) {
      errors.plate = tr("requiredField", "این فیلد الزامی است");
    }

    if (!addressForm.postalCode.trim()) {
      errors.postalCode = tr("requiredField", "این فیلد الزامی است");
    }

    if (
      addressForm.mobile &&
      !/^[0-9]{11}$/.test(normalizeDigits(addressForm.mobile))
    ) {
      errors.mobile = tr("invalidMobileError", "شماره موبایل باید ۱۱ رقم باشد");
    }

    if (
      addressForm.postalCode &&
      !/^[0-9]{10}$/.test(normalizeDigits(addressForm.postalCode))
    ) {
      errors.postalCode = tr(
        "invalidPostalCodeError",
        "کد پستی باید ۱۰ رقم باشد"
      );
    }

    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveAddress = async () => {
    if (!validateAddressForm()) return;

    setSaving(true);
    setCheckoutError("");
    setCheckoutSuccess("");
    setPaymentGuideVisible(false);

    const cleanedAddress = {
      id: editingAddressId || `addr-${Date.now()}`,
      receiverName: addressForm.receiverName.trim(),
      mobile: normalizeDigits(addressForm.mobile),
      province: addressForm.province.trim(),
      city: addressForm.city.trim(),
      fullAddress: addressForm.fullAddress.trim(),
      plate: addressForm.plate.trim(),
      unit: addressForm.unit.trim(),
      postalCode: normalizeDigits(addressForm.postalCode),
      isDefault: addresses.length === 0,
      createdAt: editingAddressId
        ? addresses.find((address) => address.id === editingAddressId)
            ?.createdAt || new Date().toISOString()
        : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    let updatedAddresses;

    if (editingAddressId) {
      updatedAddresses = addresses.map((address) =>
        address.id === editingAddressId
          ? {
              ...address,
              ...cleanedAddress,
              isDefault: address.isDefault || false,
            }
          : address
      );
    } else {
      updatedAddresses = [...addresses, cleanedAddress];
    }

    try {
      if (!isAuthenticated || !user) {
        setAddresses(updatedAddresses);
        setSelectedAddressId(cleanedAddress.id);
        setAddressMode("list");
        setAddressForm(initialAddressForm);
        return;
      }
      const result = await updateCurrentUser({
        addresses: updatedAddresses,
      });

      if (result.success) {
        setAddresses(updatedAddresses);
        setSelectedAddressId(cleanedAddress.id);
        setAddressMode("list");
        setAddressForm(initialAddressForm);

        if (refreshCurrentUser) {
          await refreshCurrentUser();
        }
      } else {
        setCheckoutError(
          result.message || tr("saveAddressError", "خطا در ذخیره آدرس")
        );
      }
    } catch (error) {
      console.error("Save address error:", error);
      setCheckoutError(tr("saveAddressError", "خطا در ذخیره آدرس"));
    } finally {
      setSaving(false);
    }
  };

  const selectAddress = async (addressId) => {
    const updatedAddresses = addresses.map((address) => ({
      ...address,
      isDefault: address.id === addressId,
    }));

    setSelectedAddressId(addressId);
    setAddresses(updatedAddresses);

    if (!isAuthenticated || !user) return;
    try {
      await updateCurrentUser({
        addresses: updatedAddresses,
      });
    } catch (error) {
      console.error("Select address error:", error);
    }
  };

  const validateCheckout = () => {
    setCheckoutError("");
    setCheckoutSuccess("");
    setPaymentGuideVisible(false);

    if (shoppingCart.length === 0) {
      setCheckoutError(tr("emptyCart", "سبد خرید شما خالی است."));
      return false;
    }

    if (!validateProfile()) {
      return false;
    }

    if (!selectedAddress) {
      setCheckoutError(
        tr("chooseAddressError", "لطفاً یک آدرس معتبر انتخاب کنید")
      );
      return false;
    }

    if (!shippingMethod) {
      setCheckoutError(
        tr("chooseShippingError", "لطفاً روش ارسال را انتخاب کنید")
      );
      return false;
    }

    return true;
  };

  const getCartStockStatus = async () => {
    const stockRows = [];

    for (const item of shoppingCart) {
      const productFromServer = await getProductById(item.id);

      const currentCount = Number(productFromServer.count || 0);
      const requestedQuantity = Number(item.quantity || 0);
      const availableQuantity = Math.min(currentCount, requestedQuantity);

      stockRows.push({
        item,
        productFromServer,
        currentCount,
        requestedQuantity,
        availableQuantity,
        isOverStock: requestedQuantity > currentCount,
      });
    }

    return stockRows;
  };

  const submitOrderWithItems = async (orderItems) => {
    if (!Array.isArray(orderItems) || orderItems.length === 0) {
      setCheckoutError(
        "هیچ کالایی با موجودی قابل ثبت سفارش در سبد شما وجود ندارد."
      );
      return false;
    }

    const now = new Date().toISOString();

    const calculatedItemsTotal = orderItems.reduce((total, item) => {
      return total + Number(item.price || 0) * Number(item.quantity || 0);
    }, 0);

    const calculatedFinalTotal = calculatedItemsTotal + shippingCost - discount;

    const order = {
      id: `order-${Date.now()}`,
      items: orderItems.map((item) => ({
        id: item.id,
        title: item.title,
        price: item.price,
        img: item.img,
        quantity: item.quantity,
        total: Number(item.price || 0) * Number(item.quantity || 0),
      })),
      customer: {
        id: user?.id || null,
        username: user?.username || "guest",
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
        phone: normalizeDigits(profileForm.phone),
        email: user?.email || "",
      },
      address: selectedAddress,
      shippingMethod,
      shippingTitle,
      deliveryTime,
      itemsTotal: calculatedItemsTotal,
      discount,
      shippingCost,
      finalTotal: calculatedFinalTotal,
      paymentStatus: "pending",
      paymentMethod: "card_to_card",
      paymentInfo: {
        cardNumber: PAYMENT_INFO.cardNumber,
        cardOwner: PAYMENT_INFO.cardOwner,
        receiptPhone: PAYMENT_INFO.receiptPhone,
        receiptChannels: PAYMENT_INFO.receiptChannels,
      },
      orderStatus: "waiting_for_payment",
      createdAt: now,
      updatedAt: now,
    };

    const result = await createOrder({
      customer: order.customer,
      address: order.address,
      items: order.items,
      shipping: { method: shippingMethod, title: shippingTitle, deliveryTime },
      subtotal: calculatedItemsTotal,
      shippingCost,
      finalTotal: calculatedFinalTotal,
    });

    emptyShoppingCart();

    setPaymentGuideVisible(true);

    setCheckoutSuccess(`سفارش شما با کد ${result.orderCode} ثبت شد. برای هماهنگی و پرداخت، پیام سفارش را در واتساپ ارسال کنید.`);

    if (refreshCurrentUser) {
      await refreshCurrentUser();
    }

    return true;
  };

  const handleContinueToPayment = async () => {
    if (!validateCheckout()) return;

    setSaving(true);
    setCheckoutError("");
    setCheckoutSuccess("");
    setPaymentGuideVisible(false);

    try {
      const stockRows = await getCartStockStatus();
      const firstOverStockItem = stockRows.find((row) => row.isOverStock);

      if (firstOverStockItem) {
        const adjustedCartItems = stockRows
          .filter((row) => row.availableQuantity > 0)
          .map((row) => ({
            ...row.item,
            price: row.productFromServer.price,
            title: row.productFromServer.title,
            img: row.productFromServer.img,
            quantity: row.availableQuantity,
          }));

        setStockModal({
          productTitle: firstOverStockItem.item.title,
          requestedQuantity: firstOverStockItem.requestedQuantity,
          availableQuantity: firstOverStockItem.currentCount,
          adjustedCartItems,
        });

        setSaving(false);
        return;
      }

      await submitOrderWithItems(shoppingCart);
    } catch (error) {
      console.error("Submit order error:", error);
      setCheckoutError(tr("submitOrderError", "خطا در ثبت سفارش"));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitAvailableStock = () => {
    if (!stockModal) return;

    const adjustedCartItems = Array.isArray(stockModal.adjustedCartItems)
      ? stockModal.adjustedCartItems
      : [];

    if (adjustedCartItems.length === 0) {
      setCheckoutError(
        "هیچ‌کدام از کالاهای انتخاب‌شده موجودی قابل ثبت سفارش ندارند. برای ثبت سفارش تعداد بیشتر، لطفاً از گزینه ثبت سفارش عمده استفاده کنید."
      );
      setStockModal(null);
      return;
    }

    replaceShoppingCart(adjustedCartItems);
    setStockModal(null);

    setPaymentGuideVisible(false);

    setCheckoutSuccess(
      "سبد خرید شما بر اساس موجودی فعلی انبار اصلاح شد. لطفاً دوباره سفارش خود را بررسی و ثبت کنید."
    );

    navigateTo("cart");
  };

  const handleGoToBulkContact = () => {
    setStockModal(null);
    navigateTo("contact");
  };

  if (authLoading) {
    return (
      <>
        <Header navigateTo={navigateTo} />
        <main className={`checkout-page ${pageDir}`}>
          <div className="checkout-loading">
            {tr("loading", "در حال بارگذاری اطلاعات...")}
          </div>
        </main>
        <Footer navigateTo={navigateTo} />
      </>
    );
  }

  return (
    <>
      <Header navigateTo={navigateTo} />

      <main className={`checkout-page ${pageDir}`}>
        <section className="checkout-hero">
          <h1>{tr("title", "ثبت سفارش")}</h1>
          <p>
            {tr(
              "subtitle",
              "اطلاعات سفارش را کامل کنید تا آماده پرداخت شود."
            )}
          </p>
        </section>

        {false ? (
          <section className="checkout-guest-card">
            <div className="guest-icon">🔐</div>
            <h2>{tr("guestTitle", "برای ثبت سفارش وارد حساب شوید")}</h2>
            <p>
              {tr(
                "guestText",
                "برای ادامه فرآیند خرید، ابتدا وارد حساب کاربری شوید یا اگر حساب ندارید ثبت‌نام کنید."
              )}
            </p>

            <div className="guest-actions">
              <button
                type="button"
                className="checkout-main-btn"
                onClick={() => navigateTo("login")}
              >
                {tr("login", "ورود به حساب")}
              </button>

              <button
                type="button"
                className="checkout-secondary-btn"
                onClick={() => navigateTo("register")}
              >
                {tr("register", "ثبت‌نام")}
              </button>
            </div>

            <button
              type="button"
              className="checkout-link-btn"
              onClick={() => navigateTo("cart")}
            >
              {tr("backToCart", "← بازگشت به سبد خرید")}
            </button>
          </section>
        ) : (
          <section className="checkout-layout">
            <div className="checkout-main-column">
              {shoppingCart.length === 0 && (
                <div className="checkout-alert warning">
                  {tr("emptyCart", "سبد خرید شما خالی است.")}
                </div>
              )}

              {checkoutError && (
                <div className="checkout-alert error">{checkoutError}</div>
              )}

              {checkoutSuccess && (
                <div className="checkout-alert success">
                  {checkoutSuccess}
                  {!isAuthenticated && <button type="button" className="checkout-secondary-btn small" onClick={() => navigateTo("register")} style={{marginRight:"12px"}}>ساخت حساب اختیاری</button>}
                </div>
              )}

              {paymentGuideVisible && (
                <div className="checkout-card">
                  <div className="checkout-card-header">
                    <h2>اطلاعات پرداخت کارت‌به‌کارت</h2>
                    <span>💳</span>
                  </div>

                  <div
                    style={{
                      lineHeight: "2.2",
                      fontSize: "15px",
                      color: "#333",
                    }}
                  >
                    <p>
                      برای نهایی شدن سفارش، مبلغ قابل پرداخت را به کارت زیر
                      واریز کنید:
                    </p>

                    <p>
                      <strong>شماره کارت:</strong>{" "}
                      <span dir="ltr">{PAYMENT_INFO.cardNumber}</span>
                    </p>

                    <p>
                      <strong>به نام:</strong> {PAYMENT_INFO.cardOwner}
                    </p>

                    <p>
                      پس از واریز، تصویر رسید پرداخت را در پیام‌رسان‌های{" "}
                      <strong>{PAYMENT_INFO.receiptChannels}</strong> به شماره
                      زیر ارسال کنید:
                    </p>

                    <p>
                      <strong>شماره ارسال رسید:</strong>{" "}
                      <span dir="ltr">{PAYMENT_INFO.receiptPhone}</span>
                    </p>

                    <p>
                      پس از بررسی رسید، وضعیت سفارش شما در پنل کاربری به{" "}
                      <strong>پرداخت شد</strong> تغییر می‌کند.
                    </p>
                  </div>
                </div>
              )}

              <div className="checkout-card">
                <div className="checkout-card-header">
                  <h2>{tr("customerInfo", "اطلاعات خریدار")}</h2>
                  <span>👤</span>
                </div>

                <div className="checkout-form-grid">
                  <label className="checkout-field">
                    <span>{tr("firstName", "نام")} *</span>
                    <input
                      type="text"
                      name="firstName"
                      value={profileForm.firstName}
                      onChange={handleProfileChange}
                    />
                  </label>

                  <label className="checkout-field">
                    <span>{tr("lastName", "نام خانوادگی")} *</span>
                    <input
                      type="text"
                      name="lastName"
                      value={profileForm.lastName}
                      onChange={handleProfileChange}
                    />
                  </label>

                  <label className="checkout-field">
                    <span>{tr("mobile", "شماره موبایل")} *</span>
                    <input
                      type="tel"
                      name="phone"
                      dir="ltr"
                      value={profileForm.phone}
                      onChange={handleProfileChange}
                      placeholder="09123456789"
                    />
                  </label>
                </div>
              </div>

              <div className="checkout-card">
                <div className="checkout-card-header">
                  <h2>{tr("deliveryAddress", "آدرس تحویل سفارش")}</h2>
                  <span>📍</span>
                </div>

                {selectedAddress ? (
                  <div className="selected-address-box">
                    <div>
                      <strong>{tr("selectedAddress", "آدرس انتخاب‌شده")}</strong>
                      <p>
                        {selectedAddress.receiverName} -{" "}
                        <span dir="ltr">{selectedAddress.mobile}</span>
                      </p>
                      <p>
                        {selectedAddress.province}، {selectedAddress.city}،{" "}
                        {selectedAddress.fullAddress}
                      </p>
                      <p>
                        {tr("plate", "پلاک")}: {selectedAddress.plate}
                        {selectedAddress.unit
                          ? `، ${tr("unit", "واحد")}: ${selectedAddress.unit}`
                          : ""}
                        {" — "}
                        {tr("postalCode", "کد پستی")}:{" "}
                        <span dir="ltr">{selectedAddress.postalCode}</span>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="no-address-box">
                    <p>
                      {tr(
                        "noAddress",
                        "هنوز آدرسی برای شما ثبت نشده است."
                      )}
                    </p>
                  </div>
                )}

                <div className="checkout-actions-row">
                  <button
                    type="button"
                    className="checkout-secondary-btn"
                    onClick={openAddressList}
                  >
                    {tr("changeAddress", "تغییر آدرس")}
                  </button>

                  <button
                    type="button"
                    className="checkout-main-btn small"
                    onClick={() => {
                      setAddressModalOpen(true);
                      openNewAddressForm();
                    }}
                  >
                    + {tr("addAddress", "افزودن آدرس جدید")}
                  </button>
                </div>
              </div>

              <div className="checkout-card">
                <div className="checkout-card-header">
                  <h2>{tr("shippingMethod", "روش ارسال")}</h2>
                  <span>🚚</span>
                </div>

                <div className="shipping-options">
                  <button
                    type="button"
                    className={`shipping-option ${
                      shippingMethod === "post" ? "active" : ""
                    }`}
                    onClick={() => setShippingMethod("post")}
                  >
                    <strong>{tr("expressPost", "پست پیشتاز")}</strong>
                    <span>
                      {tr(
                        "expressPostDesc",
                        "هزینه ارسال ۱۰۰,۰۰۰ تومان، تحویل حدود ۳ روز کاری"
                      )}
                    </span>
                  </button>

                  <button
                    type="button"
                    className={`shipping-option ${
                      shippingMethod === "courier" ? "active" : ""
                    }`}
                    onClick={() => setShippingMethod("courier")}
                  >
                    <strong>{tr("sameDayCourier", "پیک همان روز")}</strong>
                    <span>
                      {tr(
                        "sameDayCourierDesc",
                        "تحویل امروز، هزینه پیک در محل توسط مشتری پرداخت می‌شود"
                      )}
                    </span>
                  </button>
                </div>

                <div className="delivery-time-box">
                  <span>{tr("deliveryTime", "زمان تحویل")}:</span>
                  <strong>{deliveryTime}</strong>
                </div>
              </div>
            </div>

            <aside className="checkout-summary">
              <h2>{tr("orderSummary", "خلاصه سفارش")}</h2>

              <div className="summary-products">
                {shoppingCart.map((item) => (
                  <div className="summary-product" key={item.id}>
                    <span>
                      {item.title} × {item.quantity}
                    </span>
                    <strong>
                      {formatPrice(item.price * item.quantity)}{" "}
                      {tr("toman", "تومان")}
                    </strong>
                  </div>
                ))}
              </div>

              <div className="summary-line">
                <span>{tr("itemsTotal", "قیمت کالاها")}</span>
                <strong>
                  {formatPrice(itemsTotal)} {tr("toman", "تومان")}
                </strong>
              </div>

              <div className="summary-line">
                <span>{tr("discount", "تخفیف / سود خرید")}</span>
                <strong>
                  {formatPrice(discount)} {tr("toman", "تومان")}
                </strong>
              </div>

              <div className="summary-line">
                <span>{tr("shippingCost", "هزینه ارسال")}</span>
                {shippingMethod === "courier" ? (
                  <strong className="summary-note">
                    {tr(
                      "courierPayAtDoor",
                      "هزینه زمان تحویل بر عهده مشتری"
                    )}
                  </strong>
                ) : (
                  <strong>
                    {formatPrice(shippingCost)} {tr("toman", "تومان")}
                  </strong>
                )}
              </div>

              <div className="summary-final">
                <span>{tr("finalTotal", "مبلغ نهایی قابل پرداخت")}</span>
                <strong>
                  {formatPrice(finalTotal)} {tr("toman", "تومان")}
                </strong>
              </div>

              <button
                type="button"
                className="payment-btn"
                onClick={handleContinueToPayment}
                disabled={
                  saving ||
                  shoppingCart.length === 0 ||
                  !selectedAddress ||
                  !shippingMethod
                }
              >
                {saving
                  ? tr("saving", "در حال ذخیره...")
                  : "ثبت سفارش و دریافت اطلاعات پرداخت ←"}
              </button>

              <button
                type="button"
                className="checkout-link-btn full"
                onClick={() => navigateTo("cart")}
              >
                {tr("backToCart", "← بازگشت به سبد خرید")}
              </button>
            </aside>
          </section>
        )}
      </main>

      {stockModal && (
        <div className="address-modal-backdrop">
          <div className="address-modal">
            <div className="address-modal-header">
              <h3>⚠️ موجودی انبار کافی نیست</h3>

              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setStockModal(null)}
              >
                ×
              </button>
            </div>

            <div className="no-address-box">
              <p>
                تعداد درخواست ثبت سفارش شما برای محصول{" "}
                <strong>{stockModal.productTitle}</strong> بیشتر از موجودی انبار
                است.
              </p>

              <p>
                موجودی فعلی این محصول{" "}
                <strong>{stockModal.availableQuantity}</strong> عدد است، اما شما{" "}
                <strong>{stockModal.requestedQuantity}</strong> عدد انتخاب کرده‌اید.
              </p>

              <p>
                برای سفارش تعداد بیشتر، گزینه{" "}
                <strong>ثبت سفارش عمده</strong> را انتخاب کنید و درخواست خود را
                از طریق صفحه تماس با ما ارسال کنید. در غیر این صورت، گزینه{" "}
                <strong>اصلاح سبد به تعداد موجود در انبار</strong> را بزنید تا
                سبد خرید شما بر اساس موجودی فعلی اصلاح شود.
              </p>
            </div>

            <div className="modal-footer-actions">
              <button
                type="button"
                className="checkout-main-btn"
                onClick={handleSubmitAvailableStock}
                disabled={saving}
              >
                اصلاح سبد به تعداد موجود در انبار
              </button>

              <button
                type="button"
                className="checkout-secondary-btn"
                onClick={handleGoToBulkContact}
                disabled={saving}
              >
                ثبت سفارش عمده
              </button>
            </div>
          </div>
        </div>
      )}

      {addressModalOpen && (
        <div className="address-modal-backdrop">
          <div className="address-modal">
            <div className="address-modal-header">
              <h3>
                {addressMode === "list"
                  ? tr("changeAddress", "تغییر آدرس")
                  : editingAddressId
                  ? tr("editAddress", "ویرایش آدرس")
                  : tr("addAddress", "افزودن آدرس جدید")}
              </h3>

              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setAddressModalOpen(false)}
              >
                ×
              </button>
            </div>

            {addressMode === "list" ? (
              <>
                <div className="address-list">
                  {addresses.length === 0 ? (
                    <div className="no-address-box">
                      <p>
                        {tr(
                          "noAddress",
                          "هنوز آدرسی برای شما ثبت نشده است."
                        )}
                      </p>
                    </div>
                  ) : (
                    addresses.map((address) => (
                      <div
                        key={address.id}
                        className={`address-card ${
                          selectedAddressId === address.id ? "active" : ""
                        }`}
                      >
                        <div className="address-card-content">
                          <strong>{address.receiverName}</strong>
                          <p dir="ltr">{address.mobile}</p>
                          <p>
                            {address.province}، {address.city}،{" "}
                            {address.fullAddress}
                          </p>
                          <p>
                            {tr("plate", "پلاک")}: {address.plate}
                            {address.unit
                              ? `، ${tr("unit", "واحد")}: ${address.unit}`
                              : ""}
                            {" — "}
                            {tr("postalCode", "کد پستی")}:{" "}
                            <span dir="ltr">{address.postalCode}</span>
                          </p>
                        </div>

                        <div className="address-card-actions">
                          <button
                            type="button"
                            className="checkout-main-btn small"
                            onClick={() => selectAddress(address.id)}
                          >
                            {tr("selectThisAddress", "انتخاب این آدرس")}
                          </button>

                          <button
                            type="button"
                            className="checkout-secondary-btn small"
                            onClick={() => openEditAddressForm(address)}
                          >
                            {tr("editAddress", "ویرایش آدرس")}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="modal-footer-actions">
                  <button
                    type="button"
                    className="checkout-main-btn"
                    onClick={openNewAddressForm}
                  >
                    + {tr("addAddress", "افزودن آدرس جدید")}
                  </button>

                  <button
                    type="button"
                    className="checkout-secondary-btn"
                    onClick={() => setAddressModalOpen(false)}
                  >
                    {tr("close", "بستن")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="address-form-grid">
                  <label className="checkout-field">
                    <span>{tr("receiverName", "نام گیرنده")} *</span>
                    <input
                      type="text"
                      name="receiverName"
                      value={addressForm.receiverName}
                      onChange={handleAddressFormChange}
                    />
                    {addressErrors.receiverName && (
                      <small>{addressErrors.receiverName}</small>
                    )}
                  </label>

                  <label className="checkout-field">
                    <span>{tr("mobile", "شماره موبایل")} *</span>
                    <input
                      type="tel"
                      name="mobile"
                      dir="ltr"
                      value={addressForm.mobile}
                      onChange={handleAddressFormChange}
                    />
                    {addressErrors.mobile && (
                      <small>{addressErrors.mobile}</small>
                    )}
                  </label>

                  <label className="checkout-field">
                    <span>{tr("province", "استان")} *</span>
                    <input
                      type="text"
                      name="province"
                      value={addressForm.province}
                      onChange={handleAddressFormChange}
                    />
                    {addressErrors.province && (
                      <small>{addressErrors.province}</small>
                    )}
                  </label>

                  <label className="checkout-field">
                    <span>{tr("city", "شهر")} *</span>
                    <input
                      type="text"
                      name="city"
                      value={addressForm.city}
                      onChange={handleAddressFormChange}
                    />
                    {addressErrors.city && <small>{addressErrors.city}</small>}
                  </label>

                  <label className="checkout-field full">
                    <span>{tr("fullAddress", "آدرس کامل")} *</span>
                    <textarea
                      name="fullAddress"
                      value={addressForm.fullAddress}
                      onChange={handleAddressFormChange}
                      rows="3"
                    />
                    {addressErrors.fullAddress && (
                      <small>{addressErrors.fullAddress}</small>
                    )}
                  </label>

                  <label className="checkout-field">
                    <span>{tr("plate", "پلاک")} *</span>
                    <input
                      type="text"
                      name="plate"
                      value={addressForm.plate}
                      onChange={handleAddressFormChange}
                    />
                    {addressErrors.plate && (
                      <small>{addressErrors.plate}</small>
                    )}
                  </label>

                  <label className="checkout-field">
                    <span>{tr("unit", "واحد")}</span>
                    <input
                      type="text"
                      name="unit"
                      value={addressForm.unit}
                      onChange={handleAddressFormChange}
                    />
                  </label>

                  <label className="checkout-field">
                    <span>{tr("postalCode", "کد پستی")} *</span>
                    <input
                      type="text"
                      name="postalCode"
                      dir="ltr"
                      value={addressForm.postalCode}
                      onChange={handleAddressFormChange}
                    />
                    {addressErrors.postalCode && (
                      <small>{addressErrors.postalCode}</small>
                    )}
                  </label>
                </div>

                <div className="modal-footer-actions">
                  <button
                    type="button"
                    className="checkout-main-btn"
                    onClick={saveAddress}
                    disabled={saving}
                  >
                    {saving
                      ? tr("saving", "در حال ذخیره...")
                      : tr("saveAddress", "ذخیره آدرس")}
                  </button>

                  <button
                    type="button"
                    className="checkout-secondary-btn"
                    onClick={() => setAddressMode("list")}
                  >
                    {tr("cancel", "انصراف")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Footer navigateTo={navigateTo} />
    </>
  );
}
